import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowULeftDown,
  CircleNotch,
  CheckCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewReturn() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    invoice_id: '', customer_name: '', product_id: '', quantity_returned: '',
    return_reason: '', refund_amount: '', notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, salesRes] = await Promise.all([
          axios.get(`${API_URL}/api/products`, { withCredentials: true }),
          axios.get(`${API_URL}/api/sales?limit=50`, { withCredentials: true }),
        ]);
        setProducts(productsRes.data);
        setSales(salesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const selectedProduct = products.find(p => p.id === formData.product_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/returns`, {
        invoice_id: formData.invoice_id || null, customer_name: formData.customer_name,
        product_id: formData.product_id, quantity_returned: parseInt(formData.quantity_returned),
        return_reason: formData.return_reason, refund_amount: parseFloat(formData.refund_amount), notes: formData.notes
      }, { withCredentials: true });
      setSuccess(true);
      toast.success(t('returnProcessed'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process return');
    } finally { setSaving(false); }
  };

  const handleProcessAnother = () => {
    setSuccess(false);
    setFormData({ invoice_id: '', customer_name: '', product_id: '', quantity_returned: '', return_reason: '', refund_amount: '', notes: '' });
  };

  if (success) {
    return (
      <div className="max-w-md mx-auto text-center py-12 animate-slide-up" data-testid="return-success">
        <div className="w-24 h-24 bg-[#F97316]/20 mx-auto mb-6 flex items-center justify-center rounded-full">
          <CheckCircle size={60} className="text-[#F97316]" />
        </div>
        <h2 className="text-2xl font-bold text-[#F97316] mb-2" style={{ fontFamily: 'Outfit' }}>{t('returnProcessed')}</h2>
        <p className="text-[var(--dm-text-muted)] mb-8">{formData.quantity_returned} {t('unitsReturned')}</p>
        <div className="flex gap-4 justify-center">
          <Button onClick={handleProcessAnother} data-testid="process-another-btn"
            className="h-12 px-8 bg-black text-white font-semibold rounded-xl border border-[var(--dm-border)]">
            {t('processAnother')}
          </Button>
          <Button onClick={() => navigate('/')} variant="outline" className="h-12 px-8 border-[var(--dm-border)] text-[var(--dm-text-muted)] rounded-xl">
            {t('backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto animate-slide-up" data-testid="new-return-page">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#F97316]/20 mx-auto mb-4 flex items-center justify-center rounded-2xl">
          <ArrowULeftDown size={32} className="text-[#F97316]" />
        </div>
        <h2 className="text-2xl font-bold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>{t('processReturnTitle')}</h2>
        <p className="text-[var(--dm-text-muted)]">{t('returnItemsRestore')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl">
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('originalInvoice')}</Label>
          <Select value={formData.invoice_id || "none"} onValueChange={(v) => setFormData({ ...formData, invoice_id: v === "none" ? "" : v })}>
            <SelectTrigger className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" data-testid="invoice-select"><SelectValue placeholder={t('originalInvoice')} /></SelectTrigger>
            <SelectContent className="bg-[var(--card)] border-[var(--dm-border)] max-h-64">
              <SelectItem value="none">{t('noInvoice')}</SelectItem>
              {sales.map((s) => (<SelectItem key={s.id} value={s.id}>{s.invoice_number} - {s.customer_name || 'Walk-in'}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('customerName')}</Label>
          <Input value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
            data-testid="customer-name-input" placeholder="Enter customer name"
            className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" />
        </div>

        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('product')} *</Label>
          <Select value={formData.product_id} onValueChange={(v) => {
            const prod = products.find(p => p.id === v);
            setFormData({ ...formData, product_id: v, refund_amount: prod?.selling_price?.toString() || '' });
          }}>
            <SelectTrigger className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" data-testid="product-select"><SelectValue placeholder={t('selectProduct')} /></SelectTrigger>
            <SelectContent className="bg-[var(--card)] border-[var(--dm-border)] max-h-64">
              {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.product_code} - {p.name}</SelectItem>))}
            </SelectContent>
          </Select>
          {selectedProduct && (
            <p className="text-xs text-[var(--dm-text-muted)] mt-2">{t('price')}: <span className="text-[var(--primary)] font-mono">${selectedProduct.selling_price?.toFixed(2)}</span></p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('quantityReturned')} *</Label>
            <Input type="number" min="1" value={formData.quantity_returned} onChange={(e) => setFormData({ ...formData, quantity_returned: e.target.value })}
              required data-testid="quantity-input" placeholder="Qty" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12 text-lg font-mono" />
          </div>
          <div>
            <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('refundAmount')} *</Label>
            <Input type="number" step="0.01" min="0" value={formData.refund_amount} onChange={(e) => setFormData({ ...formData, refund_amount: e.target.value })}
              required data-testid="refund-input" placeholder="0.00" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12 text-lg font-mono" />
          </div>
        </div>

        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('returnReason')} *</Label>
          <Select value={formData.return_reason} onValueChange={(v) => setFormData({ ...formData, return_reason: v })}>
            <SelectTrigger className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" data-testid="reason-select"><SelectValue placeholder={t('selectReason')} /></SelectTrigger>
            <SelectContent className="bg-[var(--card)] border-[var(--dm-border)]">
              <SelectItem value="defective">{t('defective')}</SelectItem>
              <SelectItem value="wrong_item">{t('wrongItem')}</SelectItem>
              <SelectItem value="not_needed">{t('notNeeded')}</SelectItem>
              <SelectItem value="damaged">{t('damaged')}</SelectItem>
              <SelectItem value="other">{t('other')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('notes')}</Label>
          <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            data-testid="notes-input" placeholder="Optional notes..." className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl resize-none" rows={3} />
        </div>

        <Button type="submit" disabled={saving || !formData.product_id || !formData.quantity_returned || !formData.return_reason || !formData.refund_amount}
          data-testid="process-return-btn" className="w-full h-14 bg-black text-white font-bold text-lg rounded-xl hover:bg-black/80 disabled:opacity-50 border border-[var(--dm-border)]">
          {saving ? <CircleNotch size={24} className="animate-spin" /> : t('processReturnTitle')}
        </Button>
      </form>
    </div>
  );
}
